import type { ReactNode } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faXmark,
  faUpRightAndDownLeftFromCenter,
  faDownLeftAndUpRightToCenter,
} from '@fortawesome/pro-regular-svg-icons';
import { FocusTrap } from '../../../core';
import { pxToRem } from './rootFont';
import type { UseSlideOutPanelChromeResult } from './useSlideOutPanelChrome';

export interface SlideOutPanelShellProps {
  /** Whole return value of `useSlideOutPanelChrome`, passed through as one
   *  object. The hook and this component are a designed pair, so forwarding
   *  its nine fields individually made every consumer repeat the same
   *  prop-plumbing block verbatim (jscpd flagged it between panels). */
  readonly chrome: UseSlideOutPanelChromeResult;
  readonly onClose: () => void;
  /** CSS-module classes — both panels' modules export the same key set. */
  readonly styles: Readonly<Record<string, string>>;
  readonly headerTitle: ReactNode;
  readonly headerMeta?: ReactNode;
  /** Sets `headerMeta` on its own row under the title and the header's buttons, as wide as the header, so what it
   *  ends with sits under the close button. Off, it stays under the title, left of the buttons. */
  readonly metaUnderActions?: boolean;
  /** Extra header action rendered between expand and close (e.g. an
   *  "Open external page" link) — omitted when the panel has no source URL. */
  readonly headerLink?: ReactNode;
  /** Extra header action rendered before expand/close (e.g. FilePreviewPanel's
   *  "Download file"). A leading slot rather than reusing `headerLink` so the
   *  existing panels' link keeps its position next to close. */
  readonly headerActionsStart?: ReactNode;
  /** Name of the close button, for a panel whose close needs more than "Close"
   *  to be told apart (e.g. "Close details"). Defaults to "Close". */
  readonly closeLabel?: string;
  /** Leaves out the expand button, for a panel whose close is its only header control. */
  readonly hideExpand?: boolean;
  /** Joined to the header, under the title row: status pills and the panel's actions. */
  readonly headerExtra?: ReactNode;
  /** A band under the body that does not scroll with it: where the panel leads on from, or moves to the next. */
  readonly footer?: ReactNode;
  readonly children: ReactNode;
}

/**
 * Chrome shared by every right-docked citation slide-out panel (backdrop,
 * FocusTrap + dialog wrapper, resize handle, header shell with
 * expand/collapse + close, content wrapper). Extracted alongside
 * useSlideOutPanelChrome so panels that differ only in header text/link and
 * body content don't duplicate this markup (Sonar CPD flagged the
 * duplication between ThreatActorPanel and CvePanel).
 */
export function SlideOutPanelShell({
  chrome,
  onClose,
  styles,
  headerTitle,
  headerMeta,
  metaUnderActions = false,
  headerLink,
  headerActionsStart,
  closeLabel = 'Close',
  hideExpand = false,
  headerExtra,
  footer,
  children,
}: Readonly<SlideOutPanelShellProps>) {
  const {
    visible,
    panelRef,
    titleId,
    actualWidth,
    rightOffset,
    isResizing,
    isExpanded,
    toggleExpanded,
    handleResizeStart,
  } = chrome;

  return (
    <>
      {/* Dimmed backdrop — click to close */}
      <div
        className={styles.backdrop}
        data-open={visible ? '' : undefined}
        style={{
          opacity: visible ? 1 : 0,
          pointerEvents: visible ? 'auto' : 'none',
        }}
        onClick={onClose}
      />

      {/* FocusTrap ensures Tab/Shift-Tab cycle stays within the dialog while open.
          Mantine's FocusTrap puts its own ref on its child and drops the child's,
          so panelRef is handed to it as innerRef as well as set on the div. */}
      <FocusTrap active={visible} innerRef={panelRef}>
        {/* Panel — role=dialog + aria-modal for AT */}
        <div
          ref={panelRef}
          className={styles.panel}
          data-open={visible ? '' : undefined}
          role="dialog"
          aria-modal="true"
          aria-labelledby={titleId}
          tabIndex={-1}
          style={{
            width: pxToRem(actualWidth),
            right: pxToRem(rightOffset),
            opacity: visible ? 1 : 0,
            pointerEvents: visible ? 'auto' : 'none',
            transform: visible ? 'translateX(0)' : 'translateX(100%)',
          }}
        >
          {/* Resize handle */}
          <div
            className={styles.resizeHandle}
            onMouseDown={handleResizeStart}
          />

          {/* Sticky header — seeded instantly from citation metadata. The
              panel dialog is labelled by this title element (aria-labelledby). */}
          <div
            className={styles.header}
            data-joined={headerExtra ? '' : undefined}
            data-meta-wide={metaUnderActions && headerMeta ? '' : undefined}
          >
            <div className={styles.headerInfo}>
              <div id={titleId} className={styles.headerTitle}>
                {headerTitle}
              </div>
              {headerMeta && !metaUnderActions && (
                <div className={styles.headerMeta}>{headerMeta}</div>
              )}
            </div>
            <div className={styles.headerActions}>
              {headerActionsStart}
              {!hideExpand && (
                <button
                  className={styles.headerBtn}
                  onClick={toggleExpanded}
                  title={isExpanded ? 'Collapse' : 'Expand'}
                  aria-label={isExpanded ? 'Collapse panel' : 'Expand panel'}
                  type="button"
                >
                  <FontAwesomeIcon
                    icon={
                      isExpanded
                        ? faDownLeftAndUpRightToCenter
                        : faUpRightAndDownLeftFromCenter
                    }
                  />
                </button>
              )}
              {headerLink}
              <button
                className={styles.headerBtn}
                onClick={onClose}
                title={closeLabel}
                aria-label={closeLabel}
                type="button"
              >
                <FontAwesomeIcon icon={faXmark} />
              </button>
            </div>
            {headerMeta && metaUnderActions && (
              <div className={styles.headerMeta} data-wide="">
                {headerMeta}
              </div>
            )}
          </div>

          {headerExtra && (
            <div className={styles.headerExtra}>{headerExtra}</div>
          )}

          {/* Body — content is fully owned by the caller (data-gated
              loading/error/not-found/loaded states differ per source type). */}
          <div
            className={styles.content}
            style={isResizing ? { pointerEvents: 'none' } : undefined}
          >
            {children}
          </div>

          {footer && <div className={styles.footer}>{footer}</div>}
        </div>
      </FocusTrap>
    </>
  );
}
